<template>
  <div class="profile-section">
    <!-- Section navigation header -->
    <div class="profile-section__nav">
      <div class="container">
        <div class="row">
          <div class="col">
            <NavigationTabsSmooth
              class="navi-tabs"
              :handle-selection="false"
            >
              <router-link
                v-for="label in routes"
                :key="label"
                :to="{ path: basePath + label }"
                active-class="selected"
                class="vs-btn btn stylized"
                :class="(activeRoute === label) ? 'selected--computed' : ''"
              >
                <span class="without-name">{{
                  $t('profile.tabs_label.format', {
                    user: permissions.isOwner
                      ? $t(`profile.tabs_label.my`)
                      : user.profile.firstName + $t(`profile.tabs_label.apos`),
                    item: $t(`profile.tabs.${label}`, { count: 2 }),
                  })
                }}</span>
                <span class="with-name">{{ $t(`profile.tabs.${label}`, { count: 2 }) }}</span>
              </router-link>
            </NavigationTabsSmooth>
          </div>
        </div>
      </div>
    </div>

    <!-- Actual section content -->
    <div class="profile-section__main">
      <!--
        When all route failed, router-view will render Video component.
        activeRoute determine the default route that the Video component should render.

        Problems:
          1. These behaviour are controller from multiple places
          (vue-router's route and navigationGuardian (which source data from
          ProfileSection component))
          2. Currently implementation only allow fallback component to be
          rendered using a Video Component. (i.e. You cannot use other type
          of component for 404). This is ok for now since fallback
          component is always a video component. However, this will not
          work well if (e.g.) we want to use playlist as the fallback
          component.
        Questions:
          Is router-view really suitable for the job? Should it be used
          conditionally with dynamic component to support?
      -->
      <keep-alive>
        <router-view
          v-if="$route.meta.requestedTab"
          :key="$route.meta.requestedTab"
          :type="activeRoute"
          :profile-owner="user"
          :permissions="permissions"
        />

        <!-- Show default component if router fail -->
        <component
          :is="fallbackComponent"
          v-else-if="activeRoute"
          :key="'static-' + activeRoute"
          :type="activeRoute"
          :profile-owner="user"
          :permissions="permissions"
        />
      </keep-alive>
    </div>
  </div>
</template>

<script>
// Data
import NavigationTabsSmooth from '@/scripts/Components/Organisms/navigation-tabs-smooth';
import navigationGuardian from './Stuffs/profileSectionNavigationGuardian';
// TODO: Fallback content in the case where user has no tab to show. I think,
// router-view may not be best to do this. I think this is a problem because
// since Router is already handling what component should be displayed, Main.vue
// shouldn't need to include Playlists and CommonVideoWrapper and compute
// fallbackComponent here.
import Playlists from './ProfileSectionPage/Playlists/index.ts.vue';
import CommonVideoWrapper from './ProfileSectionPage/CommonVideoWrapper';

export default {
  components: {
    NavigationTabsSmooth,
    Playlists,
    CommonVideoWrapper,
  },
  props: {
    // User information
    user: {
      type: Object,
      required: true,
    },
    permissions: {
      type: Object,
      required: true,
    },
  },

  computed: {
    routes() {
      return navigationGuardian.availableRoutes;
    },

    activeRoute() {
      return this.$route.meta.requestedTab || navigationGuardian.defaultTab;
    },

    basePath() {
      return navigationGuardian.getBasePath(this.$route);
    },

    fallbackComponent() {
      if (this.activeRoute === 'playlists') {
        return 'playlists';
      } else {
        return 'common-video-wrapper';
      }
    },
  },

  beforeRouteEnter: navigationGuardian.guardNavigation,
  beforeRouteUpdate: navigationGuardian.guardNavigation,

  methods: {
    makeCount(count) {
      if (!count) return '';
      return `(${count})`;
    },
  },
};
</script>

<style lang="scss" scoped>
$_fw-normal--stylized: 500;
$tab-height: 60px;

.profile-section__nav {
  border-top: 1px solid #EEE;
  background: white;
  box-shadow: 1px 1px 1px 1px rgba(0,0,0,0.1);
}

.navi-tabs /deep/ {
  height: $tab-height;

  .btn {
    font-size: 1.2em; // Make chinese text more legible
    display: inline-block;
    text-transform: capitalize;
    padding-left: 16px;
    padding-right: 16px;
    border-bottom: 2px solid transparent;
    color: rgba(0,0,0,0.4);
    height: $tab-height;
    box-sizing: border-box;
  }

  // UX styling
  .btn {
    font-weight: normal;
    transition: color 0.1s,
                border-bottom-color 0.5s,
                ;
  }
  .btn.selected,
  .btn.selected--computed {
    font-weight: $_fw-normal--stylized;
    color: rgba(0,0,0,6);
    border-bottom-color: rgba(0,0,0,0.6);
  }

  .btn .with-name,
  .btn.selected--computed .without-name,
  .btn.selected .without-name {
    display: initial;
  }
  .btn .without-name,
  .btn.selected--computed .with-name,
  .btn.selected .with-name {
    display: none;
  }
}

.profile-section__main {
  padding-top: 32px;
  position: relative;

  /deep/ .card {
    margin: 0;
    margin-bottom: 32px;
  }
}

</style>
